import React, { useEffect, useState } from "react";
import { Text, StyleSheet, View, Platform } from "react-native";

import moment from "moment";

import cardStyles from "../cardStyles";
import CardUtils from "../CardUtils";
import SnoozeIcon from "../../../common/dialog/SnoozeIcon";
import appPages from "../../../../../appPages";
import SnoozeOptions from "../../../common/SnoozeOptions";
import ComparisonHelper from "./ComparisonHelper";
import ComparisonContent from "./ComparisonContent";
import ReflectionHelper from "../../../reflection/ReflectionHelper";
import ViewModes from "../../../reflection/header/viewModes";

const DATE_FORMAT = "YYYY-MM-DD";

export default function ComparisonCard({
  controller,
  data = { card_id: "comparison_card", type: "reflect", goal: null },
  noGoal = false, // testing
  toggleGoalJournalDialog = () => {},
  isHomePage = false,
  showMore = true,
  refreshHandler = () => {},
}) {
  const [cardData, setCardData] = useState(null);
  const [showCard, setShowCard] = useState(true);
  const [showSnoozeOptions, setShowSnoozeOptions] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedElement, setSelectedElement] = useState(null);
  const [currentDayGem, setCurrentDayGem] = useState({});
  const cardUtils = new CardUtils(controller, data, setShowCard);

  // ------------------------------------- METHODS -------------------------------------
  useEffect(() => {
    // From Comparison Page: data is already loaded
    if (!isHomePage) {
      setCardData(data);
      setSelectedDate(data.selectedDate);
      setSelectedElement(data.element);
      return;
    }

    // From Home page
    cardUtils.restoreCard(data.stored_data);

    const queryDate = ReflectionHelper.getReflectionSelectedDate({
      viewMode: ViewModes.insights,
      userSelectedDate: data.selectedDate,
      timeUnit: "week",
    }).format("YYYY-MM-DD");

    const cd = ComparisonHelper.getComparisonCardData(
      controller,
      queryDate,
      noGoal,
      data,
    );

    setCardData(cd);
    if (cd) setSelectedDate(cd.selectedDate);
  }, [data.reflection_hours]);

  useEffect(() => {
    if (!selectedDate || !cardData) return;

    // Current day gem
    const g = cardData.reflection_hours
      ? cardData.reflection_hours[selectedDate] || {}
      : {};

    setSelectedElement(cardData.element);
    setCurrentDayGem(g?.gem);
  }, [selectedDate]);

  const onDateChanged = (dt) => {
    const { date: newDate, value: target_hours } = dt;

    setCardData({ ...cardData, hours: target_hours });
    setSelectedDate(newDate);
  };

  const goToComparisonPage = () => {
    controller.navigate(appPages.comparisonPage);
  };

  const uncheckComparisonElement = (elementId, refreshHandler) => {
    ComparisonHelper.markAsSelected(controller, elementId, false, refreshHandler);
  };

  const goToReflectionDetailPage = () => {
    //TODO: !!!!!
    let model = {
      timeline: {
        days: [
          "2021-05-18",
          "2021-05-25",
          "2021-06-01",
          "2021-06-08",
          "2021-06-15",
          "2021-06-22",
        ],
        gems: [cardData.reflection_hours["2021-06-22"].gem],
        moods: [null, null, null, null, 3, 5],
      },
      edit_time_unit: cardData.time_unit || "week",
      edit_object: "element",
      edit_element_id: cardData.target_id,
      edit_day: cardData.selectedDate,
      edit_icon: cardData.target_emoji,
      edit_name: cardData.target_name,
      edit_notes: currentDayGem.note || "",
      edit_gem_mood: currentDayGem.mood || "",
      target_hours: cardData.hours || "",
      edit_id: cardData.id || "",
    };

    controller.navigate(appPages.reflectionDetailPage, {
      args: { reflection: { ...model } },
    });
  };

  const onSnooze = (option) => {
    if (option == "Write Journal") {
      showJournalDialog(currentDayGem);
    } else if (option == "More Details") {
      goToReflectionDetailPage();
    } else if (option == "Hide Comparison") {
      uncheckComparisonElement(selectedElement.id, refreshHandler);
    }

    setShowSnoozeOptions(false);
  };

  const showJournalDialog = (gem) => {
    const gemDate = gem.day || selectedDate;

    const elem = {
      ...(gem || {}),
      day: gemDate,
      item_id: selectedElement.id,
      activity: { ...selectedElement },
      time_unit: cardData.time_unit || "week",
    };

    if (!gemDate) return;

    toggleGoalJournalDialog(elem, (d) => {
      toggleGoalJournalDialog(null);

      // Refresh
      if (cardData.reflection_hours) {
        cardData.reflection_hours[gemDate] =
          cardData.reflection_hours[gemDate] || {};
        cardData.reflection_hours[gemDate].gem = {
          id: d.gem_id,
          mood: d.mood,
          note: d.text,
        };
      }

      setCardData({ ...cardData });
    });
  };

  const timeUnit = cardData && (cardData.time_unit || cardData.interval) || "week";
  const tu = timeUnit == "week" ? "isoWeek" : timeUnit;
  const cardDate = moment(selectedDate).clone().startOf(tu).format(DATE_FORMAT);
  // ------------------------------------- RENDER -------------------------------------
  return !showCard || !cardData ? null : (
    <View style={{ position: "relative" }}>
      {showSnoozeOptions ? (
        <SnoozeOptions
          style={{ opacity: 1, top: 10, right: 10 }}
          showSnoozed={true}
          customOptions={ComparisonHelper.snoozeOptions}
          onSnooze={onSnooze}
          disableOverlay
        />
      ) : null}

      <View style={cardStyles.container}>
        <View
          style={[
            cardStyles.card,
            { minHeight: 98, height: "auto", paddingBottom: 0, paddingTop: 0 },
          ]}
        >
          <SnoozeIcon
            onPress={() => setShowSnoozeOptions(true)}
            style={cardStyles.closeButton}
          />

          {/* Title */}
          {isHomePage ? (
            <View style={[cardStyles.header, { paddingHorizontal: 0 }]}>
              <View style={[cardStyles.headerInfo]}>
                <Text style={cardStyles.title}>Top Comparison</Text>
              </View>
            </View>
          ) : null}

          <ComparisonContent
            selectedDate={cardDate}
            data={cardData}
            timeUnit={cardData.time_unit || cardData.interval}
            isHomePage={isHomePage}
            showJournalDialog={showJournalDialog}
            onDateChanged={onDateChanged}
          />

          {showMore ? (
            <Text style={styles.comparisonText} onPress={goToComparisonPage}>
              See more comparisons
            </Text>
          ) : null}
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  icon: {
    width: 24,
    height: 24,
    marginRight: 6,
  },
  comparisonText: {
    fontFamily: Platform.select({
      default: "Montserrat",
      android: "Montserrat-SemiBold",
    }),
    fontWeight: "600",
    fontSize: 10,
    lineHeight: 12,
    letterSpacing: 0.175,
    color: "#8B92A1",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: 40,
  },
});
